import { Download, Loader2 } from "lucide-react";
import { useState } from "react";
import { useStripe } from "../contexts/StripeContext";
import { useFirebaseUrl } from "../hooks/useFirebaseUrl";
import { Button } from "./ui/button";
import { useToast } from "./ui/use-toast";
import { WatermarkConfirmDialog } from "./WatermarkConfirmDialog";

interface ProcessedMediaDownloaderProps {
  path: string;
  wasGeneratedWithWatermark?: boolean;
}

export function ProcessedMediaDownloader({
  path,
  wasGeneratedWithWatermark = false,
}: ProcessedMediaDownloaderProps) {
  const { url, loading, error } = useFirebaseUrl(path);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showWatermarkDialog, setShowWatermarkDialog] = useState(false);
  const { isFreePlan } = useStripe();
  const { toast } = useToast();

  const handleDownloadClick = () => {
    // Check if user is on free plan and content has watermark
    if (isFreePlan && wasGeneratedWithWatermark) {
      setShowWatermarkDialog(true);
      return;
    }

    // If not free plan or no watermark, proceed with download
    performDownload();
  };

  const performDownload = async () => {
    if (!url) return;

    setIsDownloading(true);
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.statusText}`);
      }
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      const filename = path.split("/").pop() || "download.mp4";
      link.setAttribute("download", filename);

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download failed:", err);
      toast({
        variant: "destructive",
        title: "Download Failed",
        description:
          "Could not download the video. Please check the console for more details.",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  if (loading) {
    return (
      <Button
        variant="outline"
        size="sm"
        disabled
        className="mt-1.5 h-auto py-1 px-2 text-xs"
      >
        <Download className="h-3 w-3 mr-1.5" />
        Loading...
      </Button>
    );
  }

  if (error || !url) {
    // We already log the error in the hook, so no need for another console.error here.
    return null;
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="mt-1.5 h-auto py-1 px-2 text-xs"
        onClick={handleDownloadClick}
        disabled={isDownloading}
      >
        {isDownloading ? (
          <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
        ) : (
          <Download className="h-3 w-3 mr-1.5" />
        )}
        {isDownloading ? "Downloading..." : "Download Video"}
      </Button>

      <WatermarkConfirmDialog
        isOpen={showWatermarkDialog}
        onClose={() => setShowWatermarkDialog(false)}
        onProceed={() => {
          setShowWatermarkDialog(false);
          performDownload();
        }}
        onUpgrade={() => {
          setShowWatermarkDialog(false);
          // Navigate to pricing/upgrade page
          window.location.href = "/pricing";
        }}
        action="export"
      />
    </>
  );
}
